import "@fontsource-variable/rubik";
import "@fontsource-variable/jetbrains-mono";
import "~/styles/app.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "~/App";
import type { ApiClient } from "~/api/client";
import { FakeApi } from "~/api/fake";
import { HttpApi } from "~/api/http";
import { KnowItApi } from "~/api/knowit";
import { getAccessToken, signIn, signOut } from "~/auth/oidc";
import { setAppName } from "~/foundation/hooks/useDocumentTitle";
import { initI18n } from "~/foundation/i18n";
import { SITE, SITE_CONFIG } from "~/site";
import enCatalog from "../public/locales/en/translation.json";
import heCatalog from "../public/locales/he/translation.json";

// Cross-origin handoff (cortado /post → the /market/post wizard): the public wizard carries
// its draft in the URL hash, and this stash runs BEFORE auth can redirect it away — the
// wizard then reads it from this origin's localStorage after sign-in.
try {
  if (window.location.pathname === "/market/post" && window.location.hash.startsWith("#draft=")) {
    localStorage.setItem("market.postDraft", decodeURIComponent(window.location.hash.slice(7)));
    history.replaceState(null, "", window.location.pathname);
  }
} catch {
  // Best-effort: blocked storage just means an empty wizard.
}

/**
 * The adapter is chosen by configuration, not by code:
 *
 *   VITE_API_MODE=knowit  → the real platform (Outline-style RPC, OIDC bearer token)
 *   VITE_API_URL=<origin> → the generic REST adapter
 *   neither                → in-memory fixtures, which is also how the tests run it
 */
function createApi(): ApiClient {
  const redirectToLogin = () => {
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  };

  if (import.meta.env.VITE_API_MODE === "knowit") {
    return new KnowItApi({
      // Same origin: Caddy proxies /api/* to knowit-web-api and /connect/* to knowit-auth.
      baseUrl: import.meta.env.VITE_API_URL ?? "",
      getAccessToken,
      startSignIn: (returnTo?: string, provider?: string) => signIn(returnTo, provider),
      endSession: signOut,
      onUnauthorized: () => {
        signOut();
        redirectToLogin();
      },
    });
  }

  const baseUrl = import.meta.env.VITE_API_URL;
  if (!baseUrl) {
    return new FakeApi();
  }
  return new HttpApi({ baseUrl, onUnauthorized: redirectToLogin });
}

// SLATE-LATER (§15): PWA registration and error reporting attach here, before mount.
initI18n({ en: enCatalog, he: heCatalog });

const container = document.getElementById("root");
if (!container) {
  throw new Error("Missing #root element");
}

setAppName(SITE_CONFIG.appName);
document.documentElement.dataset.site = SITE;

createRoot(container).render(
  <StrictMode>
    <App api={createApi()} />
  </StrictMode>,
);
